// Self-contained vanilla-JS analytics for the V1 marketing homepage.
//
// Used in two places (mirroring `animations.js`):
//   1. The React route /www_v1 — IndexPageV1.jsx imports this for its
//      side effect (defining window.__lfMarketingAnalyticsRun) and
//      calls the function from a useEffect once the DOM is mounted.
//      The React app already has a configured `window.gtag` from
//      App.js → @lf/analytics-events, so the bootstrap step is a no-op
//      and we only register the section/CTA/scroll listeners.
//   2. The static snapshot at lf_fe/public/www/index.html — scripts/
//      save-www-page.sh inlines this file's contents into the saved
//      HTML. The snapshot has no React, so this module bootstraps
//      gtag, Consent Mode v2 defaults, and the consent banner itself.
//
// Keep this file dependency-free and runnable as an inline <script>
// (no imports, no JSX, no ES module exports).
//
// Design source: docs/design-google-analytics.md §B (Homepage and
// marketing surfaces) and §F (Marketing & homepage event table).

(function () {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  var GA_MEASUREMENT_ID = "G-QBTFWYNDL1";
  var CONSENT_COOKIE = "lf_consent";
  var CONSENT_TTL_DAYS = 365;

  // -------- Cookie helpers ------------------------------------------------
  function readCookie(name) {
    var parts = (document.cookie || "").split(";");
    for (var i = 0; i < parts.length; i += 1) {
      var p = parts[i].replace(/^\s+/, "");
      if (p.indexOf(name + "=") === 0) {
        return decodeURIComponent(p.substring(name.length + 1));
      }
    }
    return null;
  }

  function writeCookie(name, value, days) {
    var expires = "";
    if (days) {
      var d = new Date();
      d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
      expires = "; expires=" + d.toUTCString();
    }
    var domain = "";
    try {
      var host = window.location.hostname || "";
      if (/learnforest\.app$/i.test(host)) {
        domain = "; domain=.learnforest.app";
      }
    } catch (e) {
      domain = "";
    }
    var secure = window.location.protocol === "https:" ? "; secure" : "";
    document.cookie =
      name +
      "=" +
      encodeURIComponent(value) +
      expires +
      "; path=/" +
      domain +
      secure +
      "; samesite=lax";
  }

  // -------- Bootstrap (only in standalone snapshot mode) ------------------
  function bootstrapGtagIfNeeded() {
    // If gtag is already wired (React app via @lf/analytics-events), do
    // nothing — the React/SDK path owns init + consent.
    if (typeof window.gtag === "function" && window.dataLayer) {
      return false;
    }

    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;

    // Consent Mode v2 defaults — denied until user opts in.
    gtag("consent", "default", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
      ads_data_redaction: true,
    });

    gtag("js", new Date());
    gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });

    // Async-load the gtag.js loader if not yet present.
    var loaderSrc =
      "https://www.googletagmanager.com/gtag/js?id=" + GA_MEASUREMENT_ID;
    var hasLoader = false;
    var scripts = document.getElementsByTagName("script");
    for (var i = 0; i < scripts.length; i += 1) {
      if ((scripts[i].src || "").indexOf("googletagmanager.com/gtag/js") >= 0) {
        hasLoader = true;
        break;
      }
    }
    if (!hasLoader) {
      var s = document.createElement("script");
      s.async = true;
      s.src = loaderSrc;
      var first = document.getElementsByTagName("script")[0];
      if (first && first.parentNode) {
        first.parentNode.insertBefore(s, first);
      } else {
        document.head.appendChild(s);
      }
    }

    return true;
  }

  // Tiny tracker helper used by both this module and any inline handlers
  // baked into the static snapshot. No-ops if gtag has not been wired.
  function track(name, params) {
    try {
      if (typeof window.gtag === "function") {
        window.gtag("event", name, params || {});
      }
    } catch (e) {
      // Never let analytics break the page.
    }
  }
  window.__lf_track__ = track;

  // -------- Consent (granted) handling ------------------------------------
  function applyGrantedConsent(routeId) {
    if (typeof window.gtag !== "function") return;
    window.gtag("consent", "update", {
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
      analytics_storage: "granted",
    });
    // The standalone path explicitly fires page_view since config used
    // send_page_view: false. The React path already fires page_view via
    // @lf/analytics-events on route change.
    if (!window.__lfMarketingPvFired) {
      window.__lfMarketingPvFired = true;
      track("page_view", {
        route_id: routeId || "marketing_home",
        page_location: window.location.href,
        page_path: window.location.pathname,
        page_title: document.title,
      });
    }
  }

  // -------- Consent banner (only in standalone snapshot mode) -------------
  function renderConsentBanner(routeId) {
    if (document.getElementById("lf-consent-banner")) return;

    var banner = document.createElement("div");
    banner.id = "lf-consent-banner";
    banner.setAttribute("role", "dialog");
    banner.setAttribute("aria-live", "polite");
    banner.setAttribute("aria-label", "Cookie preferences");
    banner.style.cssText = [
      "position:fixed",
      "left:16px",
      "right:16px",
      "bottom:16px",
      "z-index:2147483000",
      "max-width:560px",
      "margin-left:auto",
      "margin-right:auto",
      "background:#282846",
      "color:#fff",
      "padding:14px 16px",
      "border-radius:14px",
      "box-shadow:0 10px 40px rgba(0,0,0,0.25)",
      "font:13px/1.45 -apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,sans-serif",
      "display:flex",
      "flex-wrap:wrap",
      "align-items:center",
      "gap:10px",
    ].join(";");

    var msg = document.createElement("div");
    msg.style.cssText = "flex:1 1 240px;min-width:240px";
    msg.innerHTML =
      'We use cookies for analytics to improve Learnforest. See our ' +
      '<a href="/privacy.html" style="color:#a6e4f9;text-decoration:underline">Privacy Policy</a>.';

    var btnAccept = document.createElement("button");
    btnAccept.type = "button";
    btnAccept.textContent = "Accept";
    btnAccept.style.cssText =
      "background:#16a34a;color:#fff;border:0;border-radius:10px;padding:8px 14px;font-weight:600;cursor:pointer";

    var btnDecline = document.createElement("button");
    btnDecline.type = "button";
    btnDecline.textContent = "Decline";
    btnDecline.style.cssText =
      "background:transparent;color:#fff;border:1px solid rgba(255,255,255,0.4);border-radius:10px;padding:8px 14px;font-weight:600;cursor:pointer";

    function close() {
      if (banner && banner.parentNode) banner.parentNode.removeChild(banner);
    }

    btnAccept.addEventListener("click", function () {
      writeCookie(CONSENT_COOKIE, "granted", CONSENT_TTL_DAYS);
      applyGrantedConsent(routeId);
      close();
    });
    btnDecline.addEventListener("click", function () {
      writeCookie(CONSENT_COOKIE, "denied", CONSENT_TTL_DAYS);
      close();
    });

    banner.appendChild(msg);
    banner.appendChild(btnAccept);
    banner.appendChild(btnDecline);
    document.body.appendChild(banner);
  }

  function showConsentBanner(routeId) {
    if (document.body) {
      renderConsentBanner(routeId);
    } else {
      document.addEventListener("DOMContentLoaded", function () {
        renderConsentBanner(routeId);
      });
    }
  }
  window.__lfShowConsentBanner = showConsentBanner;

  // -------- Section view tracking -----------------------------------------
  function registerSectionObservers() {
    if (typeof IntersectionObserver === "undefined") return;
    if (window.__lfSectionObsRegistered) return;
    window.__lfSectionObsRegistered = true;

    var seen = {};
    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i += 1) {
          var entry = entries[i];
          if (!entry.isIntersecting) continue;
          if (entry.intersectionRatio < 0.5) continue;
          var el = entry.target;
          var name = el.getAttribute("data-ga-section");
          if (!name || seen[name]) continue;
          seen[name] = true;

          var docHeight = Math.max(
            document.documentElement.scrollHeight - window.innerHeight,
            1,
          );
          var positionPct = Math.round(
            (Math.min(window.scrollY || window.pageYOffset || 0, docHeight) /
              docHeight) *
              100,
          );

          track("home_section_viewed", {
            section: name,
            position_pct: positionPct,
          });
        }
      },
      { threshold: [0.5] },
    );

    var sections = document.querySelectorAll("[data-ga-section]");
    for (var i = 0; i < sections.length; i += 1) {
      observer.observe(sections[i]);
    }
  }

  // -------- CTA click tracking --------------------------------------------
  function ctaPositionFromElement(el) {
    var section = el.closest ? el.closest("[data-ga-section]") : null;
    var sectionName = section ? section.getAttribute("data-ga-section") : null;
    // Match the section ordering on /www_v1.
    var topSet = { trust_strip: 1, student: 1 };
    var bottomSet = { final_cta: 1 };
    if (sectionName && topSet[sectionName]) return "top";
    if (sectionName && bottomSet[sectionName]) return "bottom";
    return "mid";
  }

  function registerCtaDelegate() {
    if (window.__lfCtaDelegateRegistered) return;
    window.__lfCtaDelegateRegistered = true;

    document.addEventListener(
      "click",
      function (ev) {
        var target = ev.target;
        if (!target || !target.closest) return;
        var el = target.closest("[data-ga-cta]");
        if (!el) return;
        var cta = el.getAttribute("data-ga-cta");
        if (!cta) return;

        var targetUrl = "";
        if (el.tagName === "A" && el.href) {
          targetUrl = el.href;
        } else if (el.getAttribute("data-href")) {
          targetUrl = el.getAttribute("data-href");
        }

        var params = {
          cta: cta,
          target_url: targetUrl,
          position: ctaPositionFromElement(el),
        };
        var plan = el.getAttribute("data-ga-plan");
        if (plan) params.plan_subject = plan;

        track("home_cta_clicked", params);

        // Fire home_redirect_started for any CTA that navigates the user
        // off the marketing page (targetUrl is set). Modal/button CTAs
        // like plan_modal leave targetUrl empty and correctly skip.
        if (targetUrl) {
          track("home_redirect_started", {
            destination: targetUrl,
            referrer: "marketing_home",
          });
        }

        if (cta === "plan_modal" && plan) {
          track("home_plan_modal_opened", {
            plan_subject: plan,
            position: params.position,
          });
        }
      },
      true,
    );
  }

  // -------- Scroll depth tracking -----------------------------------------
  function registerScrollDepth() {
    if (window.__lfScrollDepthRegistered) return;
    window.__lfScrollDepthRegistered = true;

    var thresholds = [25, 50, 75, 100];
    var fired = {};
    var ticking = false;

    function compute() {
      ticking = false;
      var docHeight = Math.max(
        document.documentElement.scrollHeight - window.innerHeight,
        1,
      );
      var scrollTop = window.scrollY || window.pageYOffset || 0;
      var pct = Math.min(100, Math.round((scrollTop / docHeight) * 100));
      for (var i = 0; i < thresholds.length; i += 1) {
        var t = thresholds[i];
        if (!fired[t] && pct >= t) {
          fired[t] = true;
          track("home_scroll_depth", { depth_pct: t });
        }
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      if (typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(compute);
      } else {
        setTimeout(compute, 100);
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    // Fire once after first paint in case the page is already scrolled
    // (e.g. anchor link).
    compute();
  }

  // -------- Public bootstrap entry point ----------------------------------
  function run(options) {
    var routeId = (options && options.routeId) || "marketing_home";
    var didBootstrap = bootstrapGtagIfNeeded();

    // Honor existing consent cookie either way.
    var consent = readCookie(CONSENT_COOKIE);
    if (consent === "granted") {
      applyGrantedConsent(routeId);
    } else if (didBootstrap && consent !== "denied") {
      // Standalone snapshot path with no decision yet — show the banner.
      showConsentBanner(routeId);
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", function () {
        registerSectionObservers();
        registerCtaDelegate();
        registerScrollDepth();
      });
    } else {
      registerSectionObservers();
      registerCtaDelegate();
      registerScrollDepth();
    }
  }

  window.__lfMarketingAnalyticsRun = run;

  // The internal eval surfaces are not product. This module is imported for
  // its side effects by IndexPageV1, which the app bundles statically — so
  // without this guard it auto-runs on EVERY route of the parent app,
  // including /_eval/*, where it registers marketing observers and can put a
  // cookie-consent dialog over a grading queue. Internal labelling is not
  // product usage, and a funnel that counts it is measuring its own operator
  // (§4.13.6a; §4.13.3's rule running in the other direction).
  //
  // The marketing route is unaffected: IndexPageV1 calls
  // `window.__lfMarketingAnalyticsRun` itself from its own effect, and the
  // static snapshot is served at / and /www_v1, never under /_eval.
  function onAnEvalSurface() {
    try {
      var path = window.location && window.location.pathname;
      return typeof path === "string"
        && (path === "/_eval" || path.indexOf("/_eval/") === 0);
    } catch (error) {
      return false;
    }
  }

  // Auto-run when loaded standalone (e.g. inside the static snapshot).
  // The React route guards against double-run via the
  // `__lfMarketingAnalyticsRan` flag set inside IndexPageV1's useEffect.
  if (onAnEvalSurface()) {
    // Nothing. Not even the flag: a later navigation to the marketing route
    // must still be able to run it.
  } else if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      if (!window.__lfMarketingAnalyticsRan && !onAnEvalSurface()) {
        window.__lfMarketingAnalyticsRan = true;
        run();
      }
    });
  } else if (!window.__lfMarketingAnalyticsRan) {
    window.__lfMarketingAnalyticsRan = true;
    run();
  }
})();
